// Phase calculation utilities
// The phase ranges are FIXED and based on medical standards (WHO, STIKO, BZgA)

export interface PhaseRange {
  phase: number;
  min: number;
  max: number;
  display: string;
}

// FIXED phase definitions - not changeable
export const PHASE_RANGES: readonly PhaseRange[] = [
  { phase: 1, min: 0, max: 6, display: 'Woche 1-6' },
  { phase: 2, min: 7, max: 17, display: 'Monat 2-4' },
  { phase: 3, min: 18, max: 26, display: 'Monat 4-6' },
  { phase: 4, min: 27, max: 39, display: 'Monat 6-9' },
  { phase: 5, min: 40, max: 52, display: 'Monat 9-12' },
] as const;

/**
 * Calculate baby's age in weeks from birth date
 * Supports corrected age for premature babies
 */
export function calculateBabyAgeInWeeks(
  birthDate: Date,
  prematureWeeks: number = 0
): number {
  const now = new Date();
  const diffMs = now.getTime() - birthDate.getTime();
  const actualWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));

  // Corrected age for premature babies
  const correctedWeeks = Math.max(0, actualWeeks - prematureWeeks);

  return correctedWeeks;
}

/**
 * Get the current phase number based on age in weeks
 * Returns phase 1-5, defaults to phase 5 after week 52
 */
export function getCurrentPhase(ageWeeks: number): number {
  for (const range of PHASE_RANGES) {
    if (ageWeeks >= range.min && ageWeeks <= range.max) {
      return range.phase;
    }
  }
  // After week 52: keep showing phase 5
  return 5;
}

/**
 * Get the display range for a phase (e.g. "Monat 4-6")
 */
export function getPhaseDisplayRange(phaseNumber: number): string {
  const range = PHASE_RANGES.find(r => r.phase === phaseNumber);
  return range?.display ?? '';
}

/**
 * Get the week range for a phase
 */
export function getPhaseWeekRange(phaseNumber: number): { min: number; max: number } | null {
  const range = PHASE_RANGES.find(r => r.phase === phaseNumber);
  return range ? { min: range.min, max: range.max } : null;
}

/**
 * Calculate progress within the current phase (0-100)
 */
export function calculatePhaseProgress(ageWeeks: number, phaseNumber: number): number {
  const range = PHASE_RANGES.find(r => r.phase === phaseNumber);
  if (!range) return 100;

  const weeksInPhase = range.max - range.min + 1;
  const weeksCompleted = Math.max(0, Math.min(ageWeeks - range.min, weeksInPhase));

  return Math.round((weeksCompleted / weeksInPhase) * 100);
}

/**
 * Format age display based on weeks
 */
export function formatBabyAge(weeks: number): string {
  if (weeks < 1) {
    return 'Neugeboren';
  }

  const months = Math.floor(weeks / 4.33); // Average weeks per month
  const years = Math.floor(months / 12);

  if (years >= 1) {
    return `${years} ${years === 1 ? 'Jahr' : 'Jahre'}`;
  }

  if (months >= 1) {
    return `${months} ${months === 1 ? 'Monat' : 'Monate'}`;
  }

  return `${weeks} ${weeks === 1 ? 'Woche' : 'Wochen'}`;
}
